<template>
    <div>
        <div class="main-container text-left">
            <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

            <!-- Back nav -->
            <div class="req-back">
                <a class="req-back__link" href="javascript:void(0)"
                    @click="$router.push({ path: '/kids-device', query: { profileId: profileId } })">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Home
                </a>
            </div>

            <!-- Page header -->
            <div class="di-page-header">
                <div>
                    <h1 class="di-page-header__title">Device</h1>
                    <p class="di-page-header__sub">Manage and view device information</p>
                </div>
                <button class="di-sync-btn" :disabled="isDisabled" @click="syncDeviceDetails()">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" :class="{ 'di-spin': isDisabled }">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M3 3v5h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Sync
                </button>
            </div>

            <!-- ── Section: Actions ── -->
            <div class="di-section-label">Device Actions</div>
            <div class="di-card">
                <button class="di-action-btn di-action-btn--lock" :disabled="isDisabled" @click="sendDeviceCommand('LOCK')">
                    <div class="di-action-btn__icon di-action-btn__icon--orange">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="di-action-btn__body">
                        <span class="di-action-btn__title">Lock Device</span>
                        <span class="di-action-btn__sub">Remotely lock the screen</span>
                    </div>
                    <i v-if="isDisabled && currentAction === 'LOCK'" class="fa fa-spinner fa-spin di-action-btn__spin"></i>
                    <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" class="di-action-btn__arrow">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <div class="di-divider"></div>

                <button class="di-action-btn" :disabled="isDisabled" @click="sendDeviceCommand('REBOOT')">
                    <div class="di-action-btn__icon di-action-btn__icon--blue">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M3 3v5h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="di-action-btn__body">
                        <span class="di-action-btn__title">Reboot Device</span>
                        <span class="di-action-btn__sub">Restart the child's device</span>
                    </div>
                    <i v-if="isDisabled && currentAction === 'REBOOT'" class="fa fa-spinner fa-spin di-action-btn__spin"></i>
                    <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" class="di-action-btn__arrow">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <div class="di-divider"></div>
                <button class="di-action-btn" :disabled="isDisabled" @click="sendFcmCommand('SHUTDOWN')">
                    <div class="di-action-btn__icon di-action-btn__icon--red">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M18.36 6.64a9 9 0 1 1-12.73 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M12 2v10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="di-action-btn__body">
                        <span class="di-action-btn__title">Shutdown Device</span>
                        <span class="di-action-btn__sub">Remotely power off the device</span>
                    </div>
                    <i v-if="isDisabled && currentAction === 'SHUTDOWN'" class="fa fa-spinner fa-spin di-action-btn__spin"></i>
                    <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" class="di-action-btn__arrow">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <div class="di-divider"></div>

                <button class="di-action-btn" :disabled="isDisabled" @click="sendFcmCommand()">
                    <div class="di-action-btn__icon di-action-btn__icon--purple">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <circle cx="12" cy="13" r="4" stroke="currentColor" stroke-width="1.8"/>
                        </svg>
                    </div>
                    <div class="di-action-btn__body">
                        <span class="di-action-btn__title">Get Current Screen</span>
                        <span class="di-action-btn__sub">Capture device screenshot</span>
                    </div>
                    <i v-if="isDisabled && currentAction === 'GET_CURRENT_SCREEN'" class="fa fa-spinner fa-spin di-action-btn__spin"></i>
                    <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" class="di-action-btn__arrow">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <div class="di-divider"></div>

                <!-- Lost Mode -->
                <button class="di-action-btn" :disabled="isDisabled"
                    @click="lostModeEnabled ? stopLostMode() : openLostModeModal()">
                    <div class="di-action-btn__icon" :class="lostModeEnabled ? 'di-action-btn__icon--red' : 'di-action-btn__icon--orange'">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="di-action-btn__body">
                        <span class="di-action-btn__title">{{ lostModeEnabled ? 'Stop Lost Mode' : 'Start Lost Mode' }}</span>
                        <span class="di-action-btn__sub">{{ lostModeEnabled ? 'Deactivate lost mode on this device' : 'Lock device and display contact info' }}</span>
                    </div>
                    <span v-if="lostModeEnabled" class="di-lost-badge">Active</span>
                    <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" class="di-action-btn__arrow">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <!-- Lost Mode Modal -->
                <div class="di-modal-overlay" v-if="showLostModeModal" @click.self="showLostModeModal = false">
                    <div class="di-modal">
                        <div class="di-modal__header">
                            <div class="di-modal__header-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                                    <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                </svg>
                            </div>
                            <div>
                                <div class="di-modal__title">Start Lost Mode</div>
                                <div class="di-modal__sub">The device will be locked and show your contact info</div>
                            </div>
                        </div>

                        <div class="di-modal__body">
                            <div class="di-modal__field">
                                <label class="di-modal__label">Message on screen</label>
                                <textarea
                                    class="di-modal__textarea"
                                    v-model="lostModeMessage"
                                    placeholder="This device has been reported lost. Please contact the owner."
                                    rows="3"
                                    maxlength="200"
                                ></textarea>
                                <span class="di-modal__hint">Displayed on the lock screen of the lost device</span>
                            </div>

                            <div class="di-modal__field">
                                <label class="di-modal__label">Contact phone number <span class="di-modal__optional">optional</span></label>
                                <input
                                    type="tel"
                                    class="di-modal__input"
                                    v-model="lostModePhoneNumber"
                                    placeholder="+1 555 000 0000"
                                />
                            </div>

                            <div class="di-modal__field">
                                <label class="di-modal__label">Contact email <span class="di-modal__optional">optional</span></label>
                                <input
                                    type="email"
                                    class="di-modal__input"
                                    v-model="lostModeEmailAddress"
                                    placeholder="your@email.com"
                                />
                            </div>
                        </div>

                        <div class="di-modal__footer">
                            <button class="di-modal__cancel" @click="showLostModeModal = false" :disabled="isDisabled">Cancel</button>
                            <button class="di-modal__confirm" @click="startLostMode()" :disabled="isDisabled">
                                <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
                                <span v-else>Activate Lost Mode</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── Screenshot redirect notice ── -->
            <div class="di-redirect-card" v-if="showRedirectNotice">
                <div class="di-redirect-card__icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.8"/>
                        <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="di-redirect-card__body">
                    <span class="di-redirect-card__title">Screenshot Capture In Progress</span>
                    <span class="di-redirect-card__sub">Redirecting in <strong>{{ countdown }}</strong>s...</span>
                    <div class="di-progress">
                        <div class="di-progress__bar" :style="{ width: progressWidth + '%' }"></div>
                    </div>
                </div>
                <button class="di-redirect-card__cancel" @click="cancelRedirect()">Cancel</button>
            </div>

            <!-- ── Section: Controls ── -->
            <div class="di-section-label">Controls</div>
            <div class="di-card">
                <!-- Camera toggle -->
                <div class="di-toggle-row">
                    <div class="di-toggle-row__icon di-toggle-row__icon--purple">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <circle cx="12" cy="13" r="4" stroke="currentColor" stroke-width="1.8"/>
                        </svg>
                    </div>
                    <div class="di-toggle-row__info">
                        <span class="di-toggle-row__title">Camera Access</span>
                        <span class="di-toggle-row__sub">{{ cameraEnabled ? 'Camera is currently enabled' : 'Camera is currently disabled' }}</span>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="cameraEnabled" @change="toggleCamera" :disabled="isDisabled" />
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>

                <div class="di-divider"></div>
                <!-- Ring device -->
                <div class="di-toggle-row">
                    <div class="di-toggle-row__icon" :class="isPlaying ? 'di-toggle-row__icon--red' : 'di-toggle-row__icon--blue'">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="di-toggle-row__info">
                        <span class="di-toggle-row__title">{{ isPlaying ? 'Stop Sound' : 'Ring This Device' }}</span>
                        <span class="di-toggle-row__sub">{{ isPlaying ? 'Stop the ringing sound' : 'Play a sound to help locate the device' }}</span>
                    </div>
                    <button
                        class="di-ring-btn"
                        :class="isPlaying ? 'di-ring-btn--stop' : 'di-ring-btn--play'"
                        :disabled="isDisabled"
                        @click="toggleSound()">
                        <i :class="isPlaying ? 'fas fa-stop' : 'fas fa-play'"></i>
                    </button>
                </div>
            </div>

            <!-- ── Phone card ── -->
            <div class="di-phone-card" v-if="deviceDetails.phoneNumber">
                <div class="di-phone-card__icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.81a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <a :href="'tel:' + deviceDetails.phoneNumber" class="di-phone-card__number" @click="trackPhoneClick">
                    {{ formatPhoneNumber(deviceDetails.phoneNumber) }}
                </a>
                <a :href="'tel:' + deviceDetails.phoneNumber" class="di-phone-card__call" @click="trackPhoneClick">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.81a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Call
                </a>
            </div>

            <!-- ── Section: Device Details ── -->
            <div class="di-section-label">Device Details</div>

            <!-- Battery & Status -->
            <div class="di-info-card">
                <div class="di-info-card__header">
                    <div class="di-info-card__icon di-info-card__icon--green">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <rect x="2" y="7" width="16" height="10" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M22 11v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <span class="di-info-card__title">Battery & Status</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Battery Level</span>
                    <span class="di-info-row__val">{{ deviceDetails.batteryLevel }}%</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Last Checked</span>
                    <span class="di-info-row__val">{{ deviceDetails.batteryCheckedAt }}</span>
                </div>
            </div>

            <!-- Device Identity -->
            <div class="di-info-card">
                <div class="di-info-card__header">
                    <div class="di-info-card__icon di-info-card__icon--blue">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <span class="di-info-card__title">Device Identity</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Serial Number</span>
                    <span class="di-info-row__val di-info-row__val--mono">{{ deviceDetails.serialNumber }}</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">IMEI</span>
                    <span class="di-info-row__val di-info-row__val--mono">{{ deviceDetails.imei }}</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">WiFi MAC</span>
                    <span class="di-info-row__val di-info-row__val--mono">{{ deviceDetails.wifiMac }}</span>
                </div>
            </div>

            <!-- Hardware -->
            <div class="di-info-card">
                <div class="di-info-card__header">
                    <div class="di-info-card__icon di-info-card__icon--orange">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <rect x="9" y="9" width="6" height="6" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <span class="di-info-card__title">Hardware</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Model</span>
                    <span class="di-info-row__val">{{ deviceDetails.model }}</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Brand</span>
                    <span class="di-info-row__val">{{ deviceDetails.brand }}</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Manufacturer</span>
                    <span class="di-info-row__val">{{ deviceDetails.manufacturer }}</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Hardware</span>
                    <span class="di-info-row__val di-info-row__val--mono">{{ deviceDetails.hardware }}</span>
                </div>
            </div>

            <!-- System -->
            <div class="di-info-card">
                <div class="di-info-card__header">
                    <div class="di-info-card__icon di-info-card__icon--green">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M12 8v4l3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <span class="di-info-card__title">System</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Android Version</span>
                    <span class="di-info-row__val">{{ deviceDetails.androidVersion }}</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Baseband</span>
                    <span class="di-info-row__val di-info-row__val--mono">{{ deviceDetails.basebandVersion }}</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Language</span>
                    <span class="di-info-row__val">{{ deviceDetails.languageCode }}</span>
                </div>
            </div>

            <!-- Storage & Memory -->
            <div class="di-info-card">
                <div class="di-info-card__header">
                    <div class="di-info-card__icon di-info-card__icon--purple">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M22 12H2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M6 16h.01M10 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <span class="di-info-card__title">Storage & Memory</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">RAM</span>
                    <span class="di-info-row__val">{{ deviceDetails.totalRam }}</span>
                </div>
                <div class="di-info-row">
                    <span class="di-info-row__label">Storage</span>
                    <span class="di-info-row__val">{{ deviceDetails.totalStorage }}</span>
                </div>
            </div>

            <div class="last-updated" style="padding: 16px 0 24px;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                Tap Sync to refresh device information
            </div>

            <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
        </div>
    </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import KidsService from "../../services/KidsService";
import ProfileService from "../../services/ProfileService";

export default {
    name: 'DeviceOptions',
    components: { KidHeader, KidFooter },
    data() {
        return {
            profileId: this.$route.query.profileId || this.$route.query.profile_id,
            serialNumber: this.$route.query.serialNumber,
            isDisabled: false,
            currentAction: '',
            deviceDetails: {},
            showRedirectNotice: false,
            countdown: 5,
            countdownInterval: null,
            progressWidth: 0,
            cameraDisabled: false,
            cameraEnabled: true,
            profileData: {},
            isPlaying: false,
            lostModeEnabled: false,
            lostModeMessage: '',
            lostModePhoneNumber: '',
            lostModeEmailAddress: '',
            showLostModeModal: false,
        }
    },
    methods: {
        openLostModeModal() {
            this.showLostModeModal = true;
        },
        async startLostMode() {
            this.isDisabled = true;
            try {
                await KidsService.sendDeviceCommand({
                    type: 'START_LOST_MODE',
                    serialNumber: this.serialNumber,
                    lostMessage: this.lostModeMessage || 'This device has been reported lost. Please contact the owner.',
                    lostPhoneNumber: this.lostModePhoneNumber || null,
                    lostEmailAddress: this.lostModeEmailAddress || null,
                });
                this.lostModeEnabled = true;
                this.showLostModeModal = false;
                //this.$toast.success('Lost mode activated');
            } catch (err) {
                this.$toast.error(err.response?.data?.respones_msg || 'Failed to activate lost mode');
            } finally {
                this.isDisabled = false;
            }
        },
        async stopLostMode() {
            this.isDisabled = true;
            try {
                await KidsService.sendDeviceCommand({
                    type: 'STOP_LOST_MODE',
                    serialNumber: this.serialNumber,
                });
                this.lostModeEnabled = false;
                //this.$toast.success('Lost mode deactivated');
            } catch (err) {
                this.$toast.error(err.response?.data?.respones_msg || 'Failed to deactivate lost mode');
            } finally {
                this.isDisabled = false;
            }
        },
        async toggleSound() {
            this.isDisabled = true;
            const action = this.isPlaying ? 'Stop Sound' : 'Ring a Phone';
            await KidsService.sendFcmCommand({
                title: action,
                message: this.isPlaying ? 'Stop the ringing.' : 'Ring a phone to help find the device.',
                action,
                packageName: '',
                serialNumber: this.serialNumber
            })
            .then(() => { this.isPlaying = !this.isPlaying; 
                //this.$toast.success(`${action} command sent`); 
            })
            .catch((e) => { this.$toast.error(e.response?.data?.respones_msg || 'Failed'); })
            .finally(() => { this.isDisabled = false; });
        },
        formatPhoneNumber(number) {
            if (!number) return '';
            const cleaned = ('' + number).replace(/\D/g, '');
            const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
            if (match) return '(' + match[1] + ') ' + match[2] + '-' + match[3];
            return number;
        },
        trackPhoneClick() { console.log('Phone call initiated:', this.deviceDetails.phoneNumber); },
        async getProfileData() {
            await ProfileService.getDeviceProfileDetail({ profile_id: this.profileId })
            .then((response) => {
                this.profileData = response.data.data;
                this.cameraDisabled = this.profileData.camera_disabled || false;
                this.cameraEnabled = !this.cameraDisabled;
            }).catch((e) => { this.$toast.error(e.response?.data?.respones_msg || 'Error fetching profile'); });
        },
        async toggleCamera() {
            this.isDisabled = true;
            this.cameraDisabled = !this.cameraEnabled;
            try {
                const response = await KidsService.updateProfile({ profileId: this.profileId, camera_disabled: this.cameraDisabled });
                await ProfileService.applyPolicy({ profile_id: this.profileId, serialNumber: this.serialNumber });
                this.isDisabled = false;
                //this.$toast.success(response.data.respones_msg || 'Camera settings updated');
            } catch (e) {
                this.isDisabled = false;
                this.cameraEnabled = !this.cameraEnabled;
                this.cameraDisabled = !this.cameraEnabled;
                this.$toast.error(e.response?.data?.respones_msg || 'Error updating camera');
            }
        },
        async getDeviceInfo() {
            await KidsService.getDeviceInfo({ serialNumber: this.$route.query.serialNumber })
            .then((response) => { 
                this.deviceDetails = response.data.data; 
                this.lostModeEnabled = response.data.data?.enable_lost_mode ?? false;
                this.lostModeMessage = response.data.data?.enable_lost_mode_data?.lostMessage || '';
                this.lostModePhoneNumber = response.data.data?.enable_lost_mode_data?.lostPhoneNumber || '';
                this.lostModeEmailAddress = response.data.data?.enable_lost_mode_data?.lostEmailAddress || '';
            })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); this.isDisabled = false; });
        },
        async syncDeviceDetails() {
            this.isDisabled = true;
            await KidsService.syncDeviceDetails({ id: this.deviceDetails.id })
            .then((response) => { 
                //this.$toast.success(response.data.respones_msg); 
                this.getDeviceInfo(); this.isDisabled = false; 
                })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); this.isDisabled = false; });
        },
        async sendDeviceCommand(action) {
            this.isDisabled = true;
            this.currentAction = action;
            try {
                await KidsService.sendDeviceCommand({ type: action, serialNumber: this.serialNumber });
                //this.$toast.success(`${action} command sent successfully.`);
            } catch (err) {
                this.$toast.error(err.response?.data?.respones_msg || 'Failed to send command.');
            } finally {
                this.isDisabled = false;
                this.currentAction = '';
            }
        },
        async sendFcmCommand(action = null) {
            this.isDisabled = true;
            this.currentAction = action || 'GET_CURRENT_SCREEN';
            
            const actionConfig = {
                GET_CURRENT_SCREEN: {
                    title: "Send current screenshot",
                    message: "Send current screenshot",
                    action: "GET_CURRENT_SCREEN"
                },
                SHUTDOWN: {
                    title: "Shutdown Device",
                    message: "Shutdown device command sent from parent portal.",
                    action: "SHUTDOWN"
                }
            };

            const config = actionConfig[this.currentAction] || actionConfig['GET_CURRENT_SCREEN'];

            let data = {
                ...config,
                packageName: "",
                serialNumber: this.serialNumber
            };
            await KidsService.sendFcmCommand(data)
            .then((response) => {
               this.$toast.success(
                    this.currentAction === 'SHUTDOWN'
                        ? "Shutdown command sent!"
                        : "Screenshot capture command sent!"
                );
                this.isDisabled = false;

                if (this.currentAction === 'GET_CURRENT_SCREEN') {
                    this.startRedirectCountdown();
                }

                this.currentAction = '';
            }).catch((e) => {
                this.$toast.error(e.response.data.respones_msg);
                this.isDisabled = false;
                this.currentAction = '';
            });
        },
        startRedirectCountdown() {
            this.showRedirectNotice = true;
            this.countdown = 5;
            this.progressWidth = 0;
            this.countdownInterval = setInterval(() => {
                this.countdown--;
                this.progressWidth = ((5 - this.countdown) / 5) * 100;
                if (this.countdown <= 0) { this.clearCountdown(); this.redirectToScreenshots(); }
            }, 1000);
        },
        clearCountdown() { if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null; } },
        cancelRedirect() { this.clearCountdown(); this.showRedirectNotice = false; this.countdown = 5; this.progressWidth = 0; this.$toast.info('Redirect cancelled'); },
        redirectToScreenshots() { this.$router.push({ path: '/device-screenshots', query: { profileId: this.profileId, serialNumber: this.serialNumber } }); }
    },
    beforeMount() { this.getDeviceInfo(); this.getProfileData(); },
    beforeUnmount() { this.clearCountdown(); }
}
</script>

<style scoped>
/* ── Back nav ── */
.req-back { padding: 12px 14px 8px; width: 100%; text-align: left; }
.req-back__link {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600; color: #374151;
    text-decoration: none; -webkit-tap-highlight-color: transparent;
}

/* ── Page header ── */
.di-page-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 4px 14px 14px;
    width: 100%;
}
.di-page-header__title { font-size: 20px; font-weight: 700; color: #111; margin: 0 0 2px; }
.di-page-header__sub { font-size: 12px; color: #6b7280; margin: 0; }

.di-sync-btn {
    display: inline-flex; align-items: center; gap: 5px;
    height: 34px; padding: 0 12px;
    background: #f3f4f6; color: #374151;
    border: 0.5px solid #e5e7eb; border-radius: 10px;
    font-size: 12px; font-weight: 600;
    cursor: pointer; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
}
.di-sync-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.di-sync-btn:active { background: #e5e7eb; }

@keyframes spin { to { transform: rotate(360deg); } }
.di-spin { animation: spin 1s linear infinite; }

/* ── Section label ── */
.di-section-label {
    font-size: 11px; font-weight: 700; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.6px;
    padding: 14px 14px 6px;
    width: 100%;
}

/* ── Card ── */
.di-card {
    background: #fff; border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    margin: 0 14px 8px; overflow: hidden;
    width: 100%;
}

/* ── Action button row ── */
.di-action-btn {
    display: flex; align-items: center; gap: 12px;
    width: 100%; padding: 13px 14px;
    border: none; background: none;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
    transition: background 0.12s; text-align: left;
}
.di-action-btn:active:not(:disabled) { background: #f9fafb; }
.di-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.di-action-btn__icon {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.di-action-btn__icon--orange { background: #ffedd5; color: #ea580c; }
.di-action-btn__icon--blue { background: #dbeafe; color: #1d4ed8; }
.di-action-btn__icon--purple { background: #ede9fe; color: #7c3aed; }

.di-action-btn__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.di-action-btn__title { font-size: 13px; font-weight: 600; color: #111; }
.di-action-btn__sub { font-size: 11px; color: #6b7280; }
.di-action-btn__arrow { color: #d1d5db; flex-shrink: 0; }
.di-action-btn__spin { color: #6b7280; flex-shrink: 0; }

/* ── Divider ── */
.di-divider { height: 0.5px; background: #f3f4f6; margin: 0 14px; }

/* ── Redirect card ── */
.di-redirect-card {
    margin: 0 14px 8px;
    background: #eff6ff; border: 0.5px solid #bfdbfe;
    border-radius: 14px; padding: 14px;
    display: flex; align-items: center; gap: 12px;
    animation: slideDown 0.25s ease;
}
@keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

.di-redirect-card__icon {
    width: 40px; height: 40px; border-radius: 10px;
    background: #dbeafe; color: #1d4ed8;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.di-redirect-card__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.di-redirect-card__title { font-size: 13px; font-weight: 600; color: #111; }
.di-redirect-card__sub { font-size: 12px; color: #6b7280; }
.di-redirect-card__cancel {
    height: 32px; padding: 0 12px; border-radius: 8px;
    border: 0.5px solid #bfdbfe; background: #fff;
    font-size: 12px; font-weight: 600; color: #374151;
    cursor: pointer; flex-shrink: 0;
}

.di-progress {
    height: 3px; background: #bfdbfe; border-radius: 999px;
    margin-top: 8px; overflow: hidden;
}
.di-progress__bar {
    height: 100%; background: #2563eb; border-radius: 999px;
    transition: width 0.9s linear;
}

/* ── Toggle row ── */
.di-toggle-row { display: flex; align-items: center; gap: 12px; padding: 13px 14px; }
.di-toggle-row__icon {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.di-toggle-row__icon--purple { background: #ede9fe; color: #7c3aed; }
.di-toggle-row__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.di-toggle-row__title { font-size: 13px; font-weight: 600; color: #111; }
.di-toggle-row__sub { font-size: 11px; color: #6b7280; }

/* ── Switch ── */
.kid-action-switch { position: relative; display: inline-flex; width: 38px; height: 20px; cursor: pointer; margin: 0; flex-shrink: 0; }
.kid-action-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.kid-action-switch__track { position: absolute; inset: 0; background: #d1d5db; border-radius: 999px; transition: background 0.2s; }
.kid-action-switch__track::after { content: ''; position: absolute; top: 3px; left: 3px; width: 14px; height: 14px; background: white; border-radius: 50%; transition: transform 0.2s; }
.kid-action-switch input:checked + .kid-action-switch__track { background: #2563eb; }
.kid-action-switch input:checked + .kid-action-switch__track::after { transform: translateX(18px); }

/* ── Phone card ── */
.di-phone-card {
    display: flex; align-items: center; gap: 12px;
    margin: 0 14px 8px; padding: 12px 14px;
    background: #fff; border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    width: 100%;
}
.di-phone-card__icon {
    width: 36px; height: 36px; border-radius: 10px;
    background: #dcfce7; color: #16a34a;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.di-phone-card__number {
    flex: 1; font-size: 14px; font-weight: 600; color: #111;
    text-decoration: none; min-width: 0;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.di-phone-card__call {
    display: inline-flex; align-items: center; gap: 5px;
    height: 34px; padding: 0 12px;
    background: #2563eb; color: #fff;
    border-radius: 10px; text-decoration: none;
    font-size: 12px; font-weight: 600;
    flex-shrink: 0; -webkit-tap-highlight-color: transparent;
}
.di-phone-card__call:active { opacity: 0.85; }

/* ── Info card ── */
.di-info-card {
    background: #fff; border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    margin: 0 14px 8px; overflow: hidden;
    width:100%;
}
.di-info-card__header {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 14px 10px;
    border-bottom: 0.5px solid #f3f4f6;
}
.di-info-card__icon {
    width: 28px; height: 28px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.di-info-card__icon--green { background: #dcfce7; color: #16a34a; }
.di-info-card__icon--blue { background: #dbeafe; color: #1d4ed8; }
.di-info-card__icon--orange { background: #ffedd5; color: #ea580c; }
.di-info-card__icon--purple { background: #ede9fe; color: #7c3aed; }
.di-info-card__title { font-size: 13px; font-weight: 700; color: #111; }

/* ── Info row ── */
.di-info-row {
    display: flex; align-items: flex-start;
    justify-content: space-between; gap: 12px;
    padding: 10px 14px;
    border-bottom: 0.5px solid #f9fafb;
}
.di-info-row:last-child { border-bottom: none; }
.di-info-row__label { font-size: 12px; color: #6b7280; font-weight: 500; flex-shrink: 0; }
.di-info-row__val {
    font-size: 12px; font-weight: 600; color: #111;
    text-align: right; word-break: break-all;
}
.di-info-row__val--mono { font-family: monospace; font-size: 11px; }

/* ── Last updated ── */
.last-updated {
    display: flex; align-items: center; justify-content: center;
    gap: 5px; font-size: 12px; color: #9ca3af;
}

button:focus { outline: none; }

.di-toggle-row__icon--red { background: #fee2e2; color: #dc2626; }
.di-toggle-row__icon--blue { background: #dbeafe; color: #1d4ed8; }

.di-ring-btn {
    width: 34px; height: 34px; border-radius: 9px; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; flex-shrink: 0; font-size: 12px;
    -webkit-tap-highlight-color: transparent;
}
.di-ring-btn--play { background: #dbeafe; color: #1d4ed8; }
.di-ring-btn--stop { background: #fee2e2; color: #dc2626; }
.di-ring-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.di-action-btn__icon--red { background: #fee2e2; color: #dc2626; }

/* ── Lost mode badge ── */
.di-lost-badge {
    font-size: 10px; font-weight: 700;
    background: #fee2e2; color: #dc2626;
    padding: 3px 8px; border-radius: 20px;
    flex-shrink: 0;
}

/* ── Lost mode modal ── */
.di-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
    z-index: 300; display: flex; align-items: flex-end; justify-content: center;
}
.di-modal {
    background: #fff; border-radius: 20px 20px 0 0;
    width: 100%; max-width: 575px;
    padding: 20px 16px 32px;
    animation: slideUp 0.25s ease;
    max-height: 90dvh; overflow-y: auto;
}
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }

.di-modal__header {
    display: flex; align-items: flex-start; gap: 12px; margin-bottom: 18px;
}
.di-modal__header-icon {
    width: 38px; height: 38px; border-radius: 10px;
    background: #ffedd5; color: #ea580c;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.di-modal__title { font-size: 16px; font-weight: 700; color: #111; margin-bottom: 2px; }
.di-modal__sub { font-size: 12px; color: #6b7280; }

.di-modal__body { display: flex; flex-direction: column; gap: 14px; margin-bottom: 20px; }
.di-modal__field { display: flex; flex-direction: column; gap: 5px; }
.di-modal__label { font-size: 12px; font-weight: 600; color: #374151; }
.di-modal__optional { font-size: 11px; font-weight: 400; color: #9ca3af; }
.di-modal__hint { font-size: 11px; color: #9ca3af; }

.di-modal__input {
    width: 100%; height: 42px;
    border: 0.5px solid #e5e7eb; border-radius: 10px;
    padding: 0 12px; font-size: 14px; color: #111;
    background: #fafafa; outline: none; box-sizing: border-box;
}
.di-modal__input:focus { border-color: #2563eb; background: #fff; }

.di-modal__textarea {
    width: 100%; border: 0.5px solid #e5e7eb; border-radius: 10px;
    padding: 10px 12px; font-size: 14px; color: #111;
    background: #fafafa; outline: none; resize: none;
    box-sizing: border-box; line-height: 1.5;
    font-family: inherit;
}
.di-modal__textarea:focus { border-color: #2563eb; background: #fff; }

.di-modal__footer { display: flex; gap: 10px; }
.di-modal__cancel {
    flex: 1; height: 44px; border-radius: 12px;
    border: 0.5px solid #e5e7eb; background: #f3f4f6;
    font-size: 14px; font-weight: 600; color: #374151; cursor: pointer;
}
.di-modal__confirm {
    flex: 1; height: 44px; border-radius: 12px; border: none;
    background: #ea580c; color: #fff;
    font-size: 14px; font-weight: 600; cursor: pointer;
}
.di-modal__confirm:disabled, .di-modal__cancel:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 575px) {
    .di-modal { padding-bottom: calc(60px + env(safe-area-inset-bottom, 16px)); }
}
</style>